import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useLocation } from 'react-router-dom';
import { ActionGetAllOrderList } from '../../../../../store/Actions/OrderHistoryAction';
import { calculateBilling } from '../../../../../utils/billingUtils';

/**
 * useOrderHistoryLogic - Custom hook to handle order history state and logic
 */
export const useOrderHistoryLogic = () => {
    const dispatch = useDispatch();
    const { id } = useParams();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const pageParams = searchParams.get('page');
    const { order, loader } = useSelector(state => state?.OrderHistory);
    useEffect(() => {
        if (id) {
            dispatch(ActionGetAllOrderList({ Query: `/` + id }));
        }
    }, [dispatch, id]);

    const billing = useMemo(() => calculateBilling(order), [order]);

    const breadcrumbList = [
        {
            name: 'Order & History',
            linkTo: `/order-history?page=${pageParams || 1}`,
        },
        {
            name: 'Order & History List',
            linkTo: `/order-history?page=${pageParams || 1}`,
        },
        {
            name: 'Order & History Overview',
        },
    ];

    // Helper to check if sections should be shown
    const hasPatientDetails = useMemo(() =>
        order?.additionalInfo?.customerName ||
        order?.additionalInfo?.name ||
        order?.customerId?.gender ||
        order?.additionalInfo?.phoneNo ||
        order?.additionalInfo?.email
        , [order]);

    const hasAppointmentDetails = useMemo(() =>
        order?.additionalInfo?.treatment ||
        order?.additionalInfo?.treatmentData?.length > 0 ||
        order?.additionalInfo?.solutionData?.length > 0 ||
        order?.additionalInfo?.serviceData?.length > 0 ||
        order?.additionalInfo?.detoxRetreatData?.length > 0 ||
        order?.additionalInfo?.consultationType ||
        order?.additionalInfo?.totalDays ||
        (order?.additionalInfo?.startTime && order?.additionalInfo?.endTime) ||
        (order?.additionalInfo?.startDate && order?.additionalInfo?.endDate)
        , [order]);

    const dedupedHistory = useMemo(() => {
        if (!order?.orderHistorySchema) return [];

        return Object.values(
            order.orderHistorySchema.reduce((acc, item) => {
                // Case 1: Initialize → keep only the latest one
                if (item.orderStatus === "Initialize") {
                    const key = `INIT|${item.orderId}`;
                    if (!acc[key] || new Date(item.createdAt) > new Date(acc[key].createdAt)) {
                        acc[key] = item;
                    }
                    return acc;
                }

                // Case 2: Other entries → normal dedupe
                const key = [
                    item.orderId,
                    Number(item.paidAmount || 0),
                    Number(item.updatedPaidAmount || 0),
                    item.paymentStatus,
                    Number(item.amount || 0),
                    item.additionalInfo?.completedPayment,
                    item.additionalInfo?.transactionId || item._id,
                ].join("|");

                acc[key] = acc[key] || item;
                return acc;
            }, {})
        );
    }, [order?.orderHistorySchema]);

    return {
        order,
        loader,
        billing,
        breadcrumbList,
        hasPatientDetails,
        hasAppointmentDetails,
        dedupedHistory,
        id
    };
};
